import {
  calculateMetrics,
  ICalculateMetricsInputModel,
  splitMetricsByFund as splitMetricsByFundLib,
  splitMetricsByFundType as splitMetricsByFundTypeLib,
  splitMetricsByFamily as splitMetricsByFamilyLib,
  shadowFundIdByFamilyId,
  type IFamilySliceMeta,
} from '@foresightdata/metrics-calculator';
import * as Sentry from '@sentry/react';
import { ColumnState } from 'ag-grid-community';
import { ICompanyDataModel } from '../../../../data-models/company.data-model';
import { IFinanceRoundDataModel } from '../../../../data-models/finance-round.data-model';
import { IFundDataModel } from '../../../../data-models/fund.data-model';
import {
  IFundDataMetricFieldsDataModel,
  IMetricFundDataModel,
  IMetricsDataModel,
} from '../../../../data-models/metrics.data-model';
import { startOfDay } from 'date-fns';
import { IFormulaFieldDataModel } from '../../../../data-models/formula-field.data-model';
import { fundsByIdMapAtom } from '../../../../services/state/AppConfigStateJ';
import { applyFormulaFields } from '../../../../util/formula-fields';
import { getPlainDateString } from '../../../../services/queries/MaggieMetricsQueries';
import { ColumnMeta } from '../../../../types';
import { getForesightStore } from '../../../../util/jotai-store';
import { getEnvironment } from '../../../../util/environment';
import { FUND_FAMILY_COL_ID, fundFamilyLabelForRow } from './fundFamilyColumn';

export const MetricsGroupColumns: Record<string, string> = {
  Fund: 'Fund',
  FundType: 'fundType',
  FundFamily: FUND_FAMILY_COL_ID, // colId of the api-served Fund Family column (name 'FundFamily')
  InvestmentStatus: 'InvestmentStatus', // colId (coming from columnMeta.name), not path
};

export function runMetricsCalculations(
  fundsMap: Map<number, IFundDataModel>,
  metrics: IMetricsDataModel[],
  groups: ColumnState[],
  asOfDate: Date | null,
  customFundFields: ColumnMeta[] = [],
  companyMap: Map<number, ICompanyDataModel>,
  investmentMap: Map<number, IFinanceRoundDataModel>,
  // Supplied by each caller (like fundsMap/companyMap) so reactive callers
  // track the formula-fields load themselves — an untracked store read here
  // would leave atom-driven callers stale when the list resolves.
  formulaFields: IFormulaFieldDataModel[] = [],
  // Separately fetched (calculateFundTypeData: true) rows carrying `familyData`; the caller
  // only has these while the grid is grouped by Fund Family — see MetricsStateJ.ts.
  familyMetrics: IMetricsDataModel[] = []
): CalculatedMetricsDataModel[] {
  const fundsById = getForesightStore().get(fundsByIdMapAtom);
  const isGroupedByFund = hasGroupByFund(groups);
  const isGroupedByFamily = hasGroupByFundFamily(groups);
  const shadowFundIdByFamily = shadowFundIdByFamilyId(fundsById);
  let metricsData = metrics;
  if (isGroupedByFund) {
    metricsData = splitMetricsByFundLib(metrics as ICalculateMetricsInputModel[]) as IMetricsDataModel[];
  } else if (hasGroupByFundType(groups)) {
    metricsData = splitMetricsByFundTypeLib(metrics, fundsById) as IMetricsDataModel[];
  } else if (isGroupedByFamily) {
    metricsData = splitMetricsByFamilyLib(
      metrics as ICalculateMetricsInputModel[],
      fundsById
    ) as unknown as IMetricsDataModel[];
  }
  try {
    const res = calculateMetrics(
      metricsData as ICalculateMetricsInputModel[],
      fundsMap,
      companyMap,
      startOfDay(asOfDate || new Date()),
      isGroupedByFund,
      customFundFields,
      getEnvironment() === 'greycroft' /* use custom formulas for greycroft */,
      investmentMap
    );

    const calculated = res.filter(Boolean) as unknown as CalculatedMetricsDataModel[];
    const labelled =
      shadowFundIdByFamily.size === 0
        ? calculated
        : calculated.map((row) => ({
            ...row,
            fundFamily: fundFamilyLabelForRow(row as never, fundsById, shadowFundIdByFamily),
          }));
    const withFamilyFields = isGroupedByFamily
      ? overlayFamilyCustomFields(labelled, familyMetrics)
      : labelled;
    // Rows were rebuilt (fund/fundType/family splitting), so re-materialize formula
    // values against the recomputed row values. ASOFDATE() is this pass's
    // own as-of date (getPlainDateString defaults a null date to today).
    return applyFormulaFields(withFamilyFields, formulaFields, (row) => companyMap.get(row.companyId), {
      asOfDate: getPlainDateString(asOfDate),
    });
  } catch (error) {
    Sentry.captureException(error);
    return [];
  }
}

function hasGroupByFund(columns: ColumnState[]): boolean {
  return Boolean(columns?.find((column) => column.colId === MetricsGroupColumns.Fund));
}

function hasGroupByFundType(columns: ColumnState[]): boolean {
  return Boolean(columns?.find((column) => column.colId === MetricsGroupColumns.FundType));
}

function hasGroupByFundFamily(columns: ColumnState[]): boolean {
  return Boolean(columns?.find((column) => column.colId === MetricsGroupColumns.FundFamily));
}

/** A family split row's custom fund fields come from the family row, not from summing member values. */
export function overlayFamilyCustomFields<
  T extends { companyId: number; familyFundId?: number | null; _customFundFields?: Record<string, unknown> },
>(rows: T[], metrics: IMetricsDataModel[]): T[] {
  const familyRowByKey = new Map<string, IMetricFundDataModel>();
  for (const metric of metrics) {
    for (const family of metric.familyData ?? []) {
      familyRowByKey.set(`${metric.companyId}|${family.fundId}`, family);
    }
  }
  return rows.map((row) => {
    if (row.familyFundId == null) {
      return row;
    }
    const family = familyRowByKey.get(`${row.companyId}|${row.familyFundId}`);
    return family ? { ...row, _customFundFields: { ...(family.customData ?? {}) } } : row;
  });
}

export type CalculatedMetricsDataModel = IMetricsDataModel &
  IFundDataMetricFieldsDataModel &
  Partial<IFamilySliceMeta> & { formulaData?: Record<string, number | string | null>; fundFamily?: string };
